var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumberByLocale } from '../../../util';
import { Autocomplete, ConfirmDialog, ErrorMessageBox, NumberInput, UnControlledFormField } from '../../common';
import { useClientLobsAPI } from '../clients/selectors';
import { MAX_ALLOCATED_SEATS, MIN_ALLOCATED_SEATS, useBatchUpdateSeatAllocationAPI, useDeleteSeatAllocationMutationAPI, useSeatAllocationsAPI, useSeatsCapacityAPI } from './selectors';
export var SeatAllocationModal = function (_a) {
  var _b;
  var open = _a.open,
    configVersionId = _a.configVersionId,
    clientId = _a.clientId,
    clients = _a.clients,
    onClose = _a.onClose,
    onSaved = _a.onSaved;
  var t = useTranslation('orgConfig', {
    keyPrefix: 'seats'
  }).t;
  var client = clients.find(function (c) {
    return c.id === clientId;
  });
  var _c = useState(''),
    selectedLobId = _c[0],
    setSelectedLobId = _c[1];
  var _d = useState([]),
    allocationRows = _d[0],
    setAllocationRows = _d[1];
  var _e = useSeatAllocationsAPI(configVersionId),
    allocationsData = _e[0],
    fetchAllocations = _e[1];
  var _f = useClientLobsAPI(clientId, 'orgConfigModalLobs'),
    lobsData = _f[0],
    fetchLobs = _f[1];
  var _g = useBatchUpdateSeatAllocationAPI(),
    batchUpdateData = _g[0],
    batchUpdateAllocations = _g[1],
    clearBatchUpdate = _g[2];
  var _h = useDeleteSeatAllocationMutationAPI(),
    deleteData = _h[0],
    deleteAllocation = _h[1],
    clearDelete = _h[2];
  var _j = useSeatsCapacityAPI(configVersionId),
    capacityData = _j[0],
    fetchCapacity = _j[1];
  var _k = useState(false),
    pendingSaveClose = _k[0],
    setPendingSaveClose = _k[1];
  var _l = useState(null),
    pendingDeleteIndex = _l[0],
    setPendingDeleteIndex = _l[1];
  var _m = useState(null),
    confirmDeleteIndex = _m[0],
    setConfirmDeleteIndex = _m[1];
  var _o = useState(null),
    seatsError = _o[0],
    setSeatsError = _o[1];
  var lobs = useMemo(function () {
    var _a;
    return (_a = lobsData.data) !== null && _a !== void 0 ? _a : [];
  }, [lobsData.data]);
  var allAllocations = useMemo(function () {
    var _a;
    return (_a = allocationsData.data) !== null && _a !== void 0 ? _a : [];
  }, [allocationsData.data]);
  useEffect(function () {
    if (open) {
      fetchAllocations();
      fetchLobs();
      fetchCapacity();
    }
  }, [open, fetchAllocations, fetchLobs, fetchCapacity]);
  useEffect(function () {
    if (!selectedLobId) {
      setAllocationRows([]);
      return;
    }
    var relevant = allAllocations.filter(function (a) {
      return a.clientId === clientId && a.lobId === selectedLobId;
    });
    setAllocationRows(relevant.map(function (a) {
      var _a;
      return {
        allocationId: a.id,
        locationId: a.locationId,
        locationName: a.locationName,
        subLobId: a.subLobId,
        subLobName: a.subLobName,
        seats: a.allocatedSeats,
        bufferPercent: (_a = a.bufferPercent) !== null && _a !== void 0 ? _a : '',
        dirty: false
      };
    }));
  }, [selectedLobId, allAllocations, clientId]);
  useEffect(function () {
    if (lobs.length > 0 && !selectedLobId) {
      setSelectedLobId(lobs[0].id);
    }
  }, [lobs, selectedLobId]);
  useEffect(function () {
    if (!pendingSaveClose) return;
    if (batchUpdateData.requesting === false) {
      setPendingSaveClose(false);
      if (!batchUpdateData.error) {
        clearBatchUpdate();
        onSaved();
        onClose();
      }
    }
  }, [pendingSaveClose, batchUpdateData.requesting, batchUpdateData.error, clearBatchUpdate, onSaved, onClose]);
  useEffect(function () {
    if (pendingDeleteIndex === null) return;
    if (deleteData.requesting === false) {
      if (!deleteData.error) {
        setAllocationRows(function (rows) {
          return rows.filter(function (_, i) {
            return i !== pendingDeleteIndex;
          });
        });
        fetchAllocations();
        onSaved();
      }
      setPendingDeleteIndex(null);
      clearDelete();
    }
  }, [deleteData.requesting, deleteData.error, pendingDeleteIndex, fetchAllocations, clearDelete, onSaved]);
  var handleSeatsChange = function (index, value) {
    setAllocationRows(function (rows) {
      var next = __spreadArray([], rows, true);
      next[index] = __assign(__assign({}, next[index]), {
        seats: value,
        dirty: true
      });
      return next;
    });
  };
  var handleBufferChange = function (index, value) {
    setAllocationRows(function (rows) {
      var next = __spreadArray([], rows, true);
      next[index] = __assign(__assign({}, next[index]), {
        bufferPercent: value,
        dirty: true
      });
      return next;
    });
  };
  var handleDeleteRow = useCallback(function (index) {
    var row = allocationRows[index];
    setPendingDeleteIndex(index);
    deleteAllocation({}, {
      id: row.allocationId
    });
  }, [allocationRows, deleteAllocation]);
  var handleSave = useCallback(function () {
    if (!configVersionId || !selectedLobId) return;
    var dirtyRows = allocationRows.filter(function (r) {
      return r.dirty;
    });
    if (!dirtyRows.length) {
      onClose();
      return;
    }
    var seatsOutOfRange = dirtyRows.some(function (r) {
      return r.seats === '' || Number(r.seats) < MIN_ALLOCATED_SEATS || Number(r.seats) > MAX_ALLOCATED_SEATS;
    });
    if (seatsOutOfRange) {
      setSeatsError(t('validation.seatsRange', {
        min: formatNumberByLocale(MIN_ALLOCATED_SEATS),
        max: formatNumberByLocale(MAX_ALLOCATED_SEATS),
        defaultValue: 'Number of seats must be between {{min}} and {{max}}.'
      }));
      return;
    }
    setSeatsError(null);
    batchUpdateAllocations({
      updates: dirtyRows.map(function (row) {
        return {
          id: row.allocationId,
          allocatedSeats: Number(row.seats),
          bufferPercent: row.bufferPercent === '' ? null : Number(row.bufferPercent)
        };
      })
    });
    setPendingSaveClose(true);
  }, [configVersionId, selectedLobId, allocationRows, batchUpdateAllocations, onClose, t]);
  var isDeleting = pendingDeleteIndex !== null;
  return _jsxs(Dialog, __assign({
    open: open,
    onClose: onClose,
    maxWidth: "md",
    fullWidth: true
  }, {
    children: [_jsx(DialogTitle, __assign({
      variant: "h2"
    }, {
      children: t('modalTitle', {
        defaultValue: 'Seat Allocation'
      })
    })), _jsx(DialogContent, {
      children: _jsxs(Stack, __assign({
        spacing: 2,
        sx: {
          pt: 0.5
        }
      }, {
        children: [_jsx(UnControlledFormField, __assign({
          label: t('client', {
            defaultValue: 'Company/Client'
          })
        }, {
          children: _jsx(TextField, {
            fullWidth: true,
            size: "small",
            value: (_b = client === null || client === void 0 ? void 0 : client.clientName) !== null && _b !== void 0 ? _b : '',
            disabled: true,
            InputProps: {
              readOnly: true
            },
            sx: {
              bgcolor: 'action.disabledBackground'
            }
          })
        })), _jsx(UnControlledFormField, __assign({
          label: t('lob', {
            defaultValue: 'Line(s) of Business'
          })
        }, {
          children: _jsx(Autocomplete, {
            options: lobs.map(function (l) {
              return {
                id: l.id,
                name: l.lobName
              };
            }),
            placeholder: t('pleaseSelect', {
              defaultValue: 'Please select'
            }),
            value: selectedLobId || undefined,
            onChange: function (value) {
              return setSelectedLobId(value !== null && value !== void 0 ? value : '');
            }
          })
        })), _jsx(Divider, {}), allocationRows.length === 0 && selectedLobId ? _jsx(Typography, __assign({
          variant: "body2",
          color: "text.secondary"
        }, {
          children: t('noAllocationsForLob', {
            defaultValue: 'No seat reservations for this line of business.'
          })
        })) : allocationRows.map(function (row, i) {
          var _a;
          var cap = (_a = capacityData.data) === null || _a === void 0 ? void 0 : _a.find(function (c) {
            return c.locationId === row.locationId;
          });
          var availableSeatsText = '—';
          if (cap) {
            if (cap.availableSeats === null) {
              availableSeatsText = t('unlimited', {
                defaultValue: 'Unlimited'
              });
            } else {
              availableSeatsText = formatNumberByLocale(cap.availableSeats);
            }
          }
          var utilizationText = cap ? "".concat(formatNumberByLocale(cap.utilizationPercentage), "%") : '—';
          return _jsxs(Stack, __assign({
            direction: "row",
            gap: 1,
            alignItems: "flex-end"
          }, {
            children: [_jsx(UnControlledFormField, __assign({
              label: i === 0 ? t('location', {
                defaultValue: 'Site Location Name'
              }) : undefined,
              sx: {
                flex: 1
              }
            }, {
              children: _jsx(TextField, {
                fullWidth: true,
                size: "small",
                value: row.subLobName ? "".concat(row.locationName, " (").concat(row.subLobName, ")") : row.locationName,
                disabled: true,
                InputProps: {
                  readOnly: true
                },
                sx: {
                  bgcolor: 'action.disabledBackground'
                }
              })
            })), _jsx(UnControlledFormField, __assign({
              label: i === 0 ? t('seats', {
                defaultValue: '# of seats'
              }) : undefined,
              sx: {
                width: 90
              }
            }, {
              children: _jsx(NumberInput, {
                size: "small",
                min: 0,
                max: MAX_ALLOCATED_SEATS,
                value: row.seats === '' ? null : Number(row.seats),
                onChange: function (v) {
                  return handleSeatsChange(i, v == null ? '' : Number(v));
                },
                fullWidth: true
              })
            })), _jsx(UnControlledFormField, __assign({
              label: i === 0 ? t('bufferPercent', {
                defaultValue: '% buffer'
              }) : undefined,
              sx: {
                width: 90
              }
            }, {
              children: _jsx(NumberInput, {
                size: "small",
                min: 0,
                max: 100,
                decimal: 2,
                value: row.bufferPercent === '' ? null : Number(row.bufferPercent),
                onChange: function (v) {
                  return handleBufferChange(i, v == null ? '' : Number(v));
                },
                fullWidth: true,
                placeholder: t('placeholderZero', {
                  defaultValue: '0'
                })
              })
            })), _jsx(UnControlledFormField, __assign({
              label: i === 0 ? t('availableSeats', {
                defaultValue: 'Available'
              }) : undefined,
              sx: {
                width: 90
              }
            }, {
              children: _jsx(TextField, {
                size: "small",
                value: availableSeatsText,
                disabled: true,
                InputProps: {
                  readOnly: true
                },
                sx: {
                  bgcolor: 'action.disabledBackground'
                }
              })
            })), _jsx(UnControlledFormField, __assign({
              label: i === 0 ? t('utilization', {
                defaultValue: 'Util %'
              }) : undefined,
              sx: {
                width: 90
              }
            }, {
              children: _jsx(TextField, {
                size: "small",
                value: utilizationText,
                disabled: true,
                InputProps: {
                  readOnly: true
                },
                sx: {
                  bgcolor: 'action.disabledBackground',
                  input: {
                    color: 'text.secondary',
                    fontWeight: 'normal'
                  }
                }
              })
            })), _jsx(IconButton, __assign({
              size: "small",
              onClick: function () {
                return setConfirmDeleteIndex(i);
              },
              disabled: isDeleting,
              "aria-label": t('deleteSeatAllocation', {
                defaultValue: 'Delete seat allocation'
              }),
              sx: {
                mb: '9px',
                flexShrink: 0,
                color: function (theme) {
                  return theme.palette.text.primary;
                },
                '&:hover': {
                  color: function (theme) {
                    return theme.palette.text.primary;
                  },
                  backgroundColor: function (theme) {
                    return theme.palette.action.hover;
                  }
                }
              }
            }, {
              children: _jsx(FontAwesomeIcon, {
                icon: "trash-can"
              })
            }))]
          }), row.allocationId);
        }), seatsError && _jsx(Typography, __assign({
          color: "error",
          variant: "caption"
        }, {
          children: seatsError
        })), allocationsData.error && _jsx(ErrorMessageBox, {
          errors: [allocationsData.error]
        }), batchUpdateData.error && _jsx(ErrorMessageBox, {
          errors: [batchUpdateData.error]
        }), deleteData.error && _jsx(ErrorMessageBox, {
          errors: [deleteData.error]
        })]
      }))
    }), _jsxs(DialogActions, {
      children: [_jsx(Button, __assign({
        variant: "outlined",
        onClick: onClose,
        disabled: pendingSaveClose || isDeleting,
        sx: {
          textTransform: 'uppercase'
        }
      }, {
        children: t('cancel', {
          defaultValue: 'Cancel'
        })
      })), _jsx(Button, __assign({
        variant: "contained",
        onClick: handleSave,
        disabled: !selectedLobId || pendingSaveClose || isDeleting,
        sx: {
          textTransform: 'uppercase'
        }
      }, {
        children: t('save', {
          defaultValue: 'Save'
        })
      }))]
    }), _jsx(ConfirmDialog, {
      open: confirmDeleteIndex !== null,
      onClose: function () {
        return setConfirmDeleteIndex(null);
      },
      title: t('deleteAllocationTitle', {
        defaultValue: 'Delete seat allocation?'
      }),
      message: t('deleteAllocationMessage', {
        defaultValue: 'Are you sure you want to delete this seat allocation? This cannot be undone.'
      }),
      onConfirm: function () {
        if (confirmDeleteIndex !== null) {
          handleDeleteRow(confirmDeleteIndex);
          setConfirmDeleteIndex(null);
        }
      }
    })]
  }));
};